
import { defineComponent, onMounted, ref, watch, onUnmounted } from 'vue';
import BaseLayout from '@/layouts/BaseLayout.vue';
import HeaderBar from '@/components/common/HeaderBar.vue';
import { useRoute } from 'vue-router';
import busHandler from '@/api-handler/bus';
import { IBusRoute, IBusStopOfRoute, IStop, IBusN1EstimateTime, IOperator } from '@/types/api/bus';
import { taiwanCity } from '@/utils/cities';
import { City, DirectionType, ILatLngLiteral, StopStatusType } from '@/types';
import { Toast } from "@/utils/toast-helper";
import RouteMap from '@/components/common/RouteMap.vue';
import BusRouteInfoDialog from '@/components/common/BusRouteInfoDialog.vue';
import BusRouteMapDialog from '@/components/common/BusRouteMapDialog.vue';
import LoadingPage from '@/components/common/LoadingPage.vue';
import router from '@/router';

interface IwholeInfoType extends IStop {
  StopStatus: StopStatusType;
  EstimateTime: number | null;
}

interface IPosionAndName extends ILatLngLiteral {
  name: string;
}

export default defineComponent({
  name: 'BusRouteStausInfo',
  components: {
    BaseLayout,
    HeaderBar,
    BusRouteInfoDialog,
    BusRouteMapDialog,
    RouteMap,
    LoadingPage,
  },
  setup() {
    const cityList = taiwanCity;
    const route = useRoute();
    const { city, busName } = route.params;
    const currentCity = ref<string>('');
    const busRouteInfo = ref<IBusRoute[]>([]);
    const busStopOfRoute = ref<IBusStopOfRoute[]>([]);
    const busEstimatedTimeOfArrival = ref<IBusN1EstimateTime[]>([]);
    const selectedBusDirection = ref<DirectionType>(DirectionType.Outbound);
    const wholeBusStops = ref<IwholeInfoType[]>([]);
    const isShowRouteInfo = ref<boolean>(false);
    const isShowRouteMap = ref<boolean>(false);
    const busOperatorName = ref<string>('');
    const busOperatorInfo = ref<IOperator | null>(null);
    const geometryArray = ref<IPosionAndName[]>([]);
    const currentRoute = ref<IStop[]>([]);
    const currentSecond = ref<number>(30);
    const isLoading = ref<boolean>(true);
    let intervalTimer: ReturnType<typeof setInterval> | undefined;


    const getCurrentCity = (cityEnglishName: string) => {
      cityList.forEach((city: City) => {
        if (cityEnglishName === city.value) {
          currentCity.value = city.name;
        }
      });
    };
    const getBusStopOfRoute = async() => {
      try {
        const res = await busHandler.getBusStopOfRoute(city as string, busName as string);
        if (res) {
          busStopOfRoute.value = res.data;
        }
      } catch (error) {
        console.log(error);
      }
    };
    const getBusRouteInfo = async() => {
      try {
        const res = await busHandler.getRouteInfo(city as string, busName as string);
        if (res) {
          busRouteInfo.value = await res.data;
          busOperatorName.value = busRouteInfo.value[0].Operators[0].OperatorName.Zh_tw
          getOperaterInfo();
        }
      } catch (error) {
        console.log(error);
      }
    };

    const getBusEstimatedTimeOfArrival = async() => {
      try {
        const res = await busHandler.getBusEstimatedTimeOfArrival(city as string, busName as string);
        if (res) {
          busEstimatedTimeOfArrival.value = res.data;
        }
      } catch (error) {
        console.log(error);
      }
    };

    const changeBusDirection = (type: string) => {
      selectedBusDirection.value = (DirectionType as { [key: string]: any })[type];
    };

    const getWholeBusStops = async() => {
      try {
        isLoading.value = true;
        await getBusEstimatedTimeOfArrival();
        // 預設為去程路線
        currentRoute.value = selectedBusDirection.value === DirectionType.Outbound ? busStopOfRoute.value[DirectionType.Outbound].Stops : busStopOfRoute.value[DirectionType.Return].Stops;
        const currentDirectionArriveTimeInfo = busEstimatedTimeOfArrival.value.filter((info) => info.Direction === selectedBusDirection.value);
        wholeBusStops.value = currentRoute.value.reduce((result: IwholeInfoType[], route: IStop) => {
          for(let arriveTimeInfo of currentDirectionArriveTimeInfo) {
            if (arriveTimeInfo.StopID === route.StopID) {
              const wholeInfo: IwholeInfoType = {
                ...route,
                StopStatus: arriveTimeInfo.StopStatus as StopStatusType,
                EstimateTime: arriveTimeInfo.EstimateTime ? arriveTimeInfo.EstimateTime : null
              }
              result.push(wholeInfo);
            }
          }
          return result;
        }, []);
        formatGeometry(currentRoute.value);
        isLoading.value = false;
        secondCountDown();
      } catch (error) {
        console.log(error);
        isLoading.value = false;
      }
    };

    const statusFilter = (status: StopStatusType) => {
      switch(status) {
        case StopStatusType.NoDeparture:
          return '尚未發車';
        case StopStatusType.NonStop:
          return '交管不停靠';
        case StopStatusType.LastBusPassed:
          return '末班車已過';
        case StopStatusType.NonOperation:
          return '今日未營運';
      }
    };

    const estimatedArriveTimeFilter = (time: number) => {
      if (time < 60) {
        return '進站中';
      } else if (time < 120) {
        return '即將進站';
      } else {
        const minute = Math.ceil(time / 60);
        return `${minute} 分鐘`;
      }
    };

    const getStopStatusClass = (time: number) => {
      if (time < 60) {
        return 'pulling';
      } else if (time < 120) {
        return 'about-arrive';
      } else if (time >= 120) {
        return '';
      }
    };

    const getOperaterInfo = async() => {
      try {
        const res = await busHandler.getBusOperatorInfo(city as string, busOperatorName.value);
        if (res) {
          busOperatorInfo.value = res.data[0];
        }
      } catch (error) {
        console.log(error);
      }
    };

    const updateRouteStatus = async() => {
      await getWholeBusStops();
      Toast.fire({
        icon: 'success',
        title: '已更新成功！'
      });
    };

    const secondCountDown = () => {
      // 每30秒fetch一次資料
      clearInterval(intervalTimer);
      currentSecond.value = 30;
      intervalTimer = setInterval(() => {
        if (currentSecond.value == 0) {
          currentSecond.value = 30;
        }
        currentSecond.value -= 1;
      }, 1000);
    };

    const openRouteInfo = () => {
      isShowRouteInfo.value = true;
    };

    const formatGeometry = (stops: IStop[]) => {
      geometryArray.value = stops.map((stop) => ({
        lat: stop.StopPosition.PositionLat as number,
        lng: stop.StopPosition.PositionLon as number,
        name: stop.StopName.Zh_tw
      }))
    };

    const goStopInfo = (stopInfo: IStop) => {
      router.push(`/stop-info/${city}/${stopInfo.StopName.Zh_tw}`);
    };

    const sharePageInfo = () => {
      const string = `查看等等公車的${currentCity.value} ${busName}公車 即時資訊： https://jolinhappy.github.io/find-bus-app/#${route.fullPath}`
      navigator.clipboard.writeText(string)
      Toast.fire({
        icon: 'success',
        title: '太棒了！ 複製成功！'
      });
    };

    watch(() => selectedBusDirection.value, async() => {
      getWholeBusStops();
    });

    onMounted(async() => {
      getBusRouteInfo();
      getBusStopOfRoute();
      getWholeBusStops();
    });
    onUnmounted(() => {
      clearInterval(intervalTimer);
    });
    return{
      busRouteInfo,
      busStopOfRoute,
      currentCity,
      city,
      busName,
      selectedBusDirection,
      wholeBusStops,
      isShowRouteInfo,
      isShowRouteMap,
      busOperatorInfo,
      geometryArray,
      currentRoute,
      currentSecond,
      isLoading,
      getCurrentCity,
      changeBusDirection,
      statusFilter,
      estimatedArriveTimeFilter,
      getStopStatusClass,
      getWholeBusStops,
      updateRouteStatus,
      openRouteInfo,
      getBusRouteInfo,
      getBusStopOfRoute,
      formatGeometry,
      goStopInfo,
      sharePageInfo,
    }
  },
});
